import { DetectDevicesService } from '@fi-shared/fi-shared-services/detect-devices.service';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { IStatistic2ReportItem, IStatistic2ReportParameter } from '@fi-app/statistic2/statistic2';
import {
    Statistic2ReportType,
    Statistic2ResultsPerType,
    Statistic2UserSelectionType,
} from '@fi-app/statistic2/statistic2-types';
import { BehaviorSubject, Subject } from 'rxjs';
import { cloneDeep } from '@fi-shared/utils';
import { Statistic2HelperService } from '@fi-app/statistic2/statistic2-helper.service';
import { takeUntil } from 'rxjs/operators';
import {
    IStatistic2ReportSchemaItem,
    IStatistic2ReportViewParams,
} from '@fi-app/statistic2/statistic2-report-view/statistic2-report-view';
import { AppTextService } from '@fi-shared/app-text';
import { Statistic2Service } from '@fi-app/statistic2/statistic2.service';
import { FiPrintDownloadComponent } from '@fi-shared/fi-print-download/fi-print-download.component';
import { Statistic2ReportParameterEditDialogComponent } from '@fi-app/statistic2/statistic2-report-parameter-edit/statistic2-report-parameter-edit-dialog/statistic2-report-parameter-edit-dialog.component';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { FiDateFormatOptions } from '@fi-shared/utils/format-date';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { ObjectTypesService } from '@fi-app/utilities/fi-services/object-types.service';
import { Statistic2ReportService } from '@fi-app/statistic2/statistic2-report.service';
import { Statistic2ReportColumn } from '@fi-app/statistic2/statistic2-report-view/statistic2-report-column';
import { Statistic2ReportDetailsType } from '@fi-app/statistic2/statistic2-report-view/statistic2-report-details-type';
import { Statistic2ReportViewListComponent } from '@fi-app/statistic2/statistic2-report-view/statistic2-report-view-list/statistic2-report-view-list.component';
import { MatSortDirection, ObjectTypes, SortDirection } from '@fi-shared/types';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { UserService } from '@fi-app/user/user.service';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-statistic2-report-view',
    templateUrl: './statistic2-report-view.component.html',
    styleUrls: ['./statistic2-report-view.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-statistic2-report-view' },
    standalone: false,
})
export class Statistic2ReportViewComponent implements OnInit, OnDestroy {
    @Input('reportParameter') initialReportParameter: IStatistic2ReportParameter;
    @Input() showPrevReport$: Subject<void> = new Subject<void>();
    @Output() initialReportParameterChanged = new EventEmitter<boolean>();
    @ViewChild(FiPrintDownloadComponent, { static: false }) printDownloadComponent: FiPrintDownloadComponent;

    @ViewChild(Statistic2ReportViewListComponent, { static: false })
    reportViewListComponent: Statistic2ReportViewListComponent;

    menuActions = [
        {
            title: 'statistic.edit_report',
            onClick: () => {
                this.editReport(this.initialReportParameter);
            },
        },
        {
            title: 'statistic.delete_report',
            onClick: () => {
                this.statistic2HelperService.showDeleteReportDialog().onYesButtonClick(() => {
                    this.deleteReport();
                });
            },
        },
    ];
    EditTabs: EditTabsModel;

    showReportViewList$ = new BehaviorSubject<boolean>(false);
    filterText$ = new BehaviorSubject<string>('');

    timePeriod: string;
    periodText: string;

    private reportViewParamsStack: IStatistic2ReportViewParams[] = [];

    constructor(
        private statistic2Service: Statistic2Service,
        private statistic2HelperService: Statistic2HelperService,
        private appTextService: AppTextService,
        private userService: UserService,
        private fiLoadingService: LoadingService,
        private fiStateService: FiStateService,
        private objectTypesService: ObjectTypesService,
        private detectDeviceService: DetectDevicesService,
        private statistic2ReportService: Statistic2ReportService,
        private pageTitleService: PageTitleService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit(): void {
        this.showResultsPer = this.initialReportParameter.ShowResultsPerType;
        this.reportViewParamsStack.push({
            reportParameter: this.initialReportParameter,
        });
        this.onReportParameterChanged();

        this.showPrevReport$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            if (this.reportViewParamsStack.length > 1) {
                this.reportViewParamsStack.pop();
                this.onReportParameterChanged();
            }
        });

        if (this.statistic2HelperService.getSelectedReportItemIndex()) {
            this.redirectToSpectificItem(this.statistic2HelperService.getSelectedReportItemIndex());
        }

        this.EditTabs = new EditTabsModel('', this.appTextService.get('back'));

        this.pageTitleService.setTitleByName(this.initialReportParameter.Name, ObjectTypes.Statistic2);
    }

    isMobileScreenWide() {
        return this.detectDeviceService.isMobilePhone();
    }

    isMobileScreenSmall() {
        return window.matchMedia('(max-width: 550px)').matches;
    }

    editReport(reportParameter: IStatistic2ReportParameter) {
        this.statistic2HelperService
            .showEditReportDialog(
                'statistic.edit_statistics_report',
                Statistic2ReportParameterEditDialogComponent,
                cloneDeep(reportParameter),
            )
            .onYesButtonClick((editedReportParameter) => {
                editedReportParameter.DataRepresentationTypeView = editedReportParameter.DataRepresentationType;
                this.fiLoadingService
                    .trackPromise(this.statistic2Service.saveReportParameter(editedReportParameter))
                    .then(() => {
                        this.fiStateService.go(this.fiStateService.current.name, this.fiStateService.current.params, {
                            reload: true,
                        });
                    });
            });
    }

    deleteReport() {
        this.fiLoadingService
            .trackPromise(this.statistic2Service.deleteReportParameters([this.initialReportParameter]))
            .then(() => this.fiStateService.go(FiStateNames.Statistic2.Dashboard));
    }

    getCurrentReportViewParams() {
        if (!this.reportViewParamsStack.length) throw 'reportViewParamsStack is empty';
        return this.reportViewParamsStack[this.reportViewParamsStack.length - 1];
    }

    getPreviousReportViewParams() {
        if (this.reportViewParamsStack.length > 1)
            return this.reportViewParamsStack[this.reportViewParamsStack.length - 2];
    }

    private onReportParameterChanged() {
        this.initialReportParameterChanged.next(this.reportViewParamsStack.length === 1);

        const viewParams = this.getCurrentReportViewParams();
        const currentReportParameter = viewParams.reportParameter;
        const isUserDetailedReport = this.statistic2HelperService.isUserDetailedReport(currentReportParameter);
        const isUserNoGropingReport = this.statistic2HelperService.isUserNoGroupingReport(currentReportParameter);

        viewParams.loadItemsPromise = (() => {
            if (viewParams.loadedData) {
                return Promise.resolve(viewParams.loadedData);
            }
            return this.statistic2Service.getReportData(currentReportParameter, true);
        })();

        if (!viewParams.loadedData) {
            viewParams.loadItemsPromise.then((data) => (viewParams.loadedData = data));
        }

        viewParams.loadItemsPromise.then((data) => {
            if (data.StartDate && data.EndDate) {
                viewParams.details.find((item) => item.type == Statistic2ReportDetailsType.ReportFromDate).value =
                    this.fiFormatDateService.formatDate(data.StartDate, FiDateFormatOptions);
                viewParams.details.find((item) => item.type == Statistic2ReportDetailsType.ReportToDate).value =
                    this.fiFormatDateService.formatDate(data.EndDate, FiDateFormatOptions);
                this.periodText =
                    this.fiFormatDateService.formatDate(data.StartDate, FiDateFormatOptions) +
                    ' - ' +
                    this.fiFormatDateService.formatDate(data.EndDate, FiDateFormatOptions);
            }
        });

        //  When viewing this page: logins per unit → unit
        //  hide area & unit columns
        //const isOnlyOneUnitSelection = isUserDetailedReport && (currentReportParameter.BusinessUnitIds || []).length === 1 && this.reportViewParamsStack.length !== 1;
        viewParams.schema = [];
        if (isUserDetailedReport || this.isStatisticsReportPerUser(currentReportParameter)) {
            viewParams.schema.push({ columnType: Statistic2ReportColumn.Icon, columnHeader: '' });
        }
        if (!isUserNoGropingReport) {
            let objectNameHeader = this.statistic2HelperService.getShowResultsPerText(
                this.getResultPerType(viewParams, currentReportParameter),
            );
            viewParams.schema.push({ columnType: Statistic2ReportColumn.ObjectName, columnHeader: objectNameHeader });
        }
        viewParams.showAreaColumn =
            currentReportParameter.ShowResultsPerType === Statistic2ResultsPerType.Unit ||
            isUserDetailedReport /* && !isOnlyOneUnitSelection*/;
        if (viewParams.showAreaColumn) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.Area,
                columnHeader: this.appTextService.get('common.area'),
            });
        }
        if (isUserDetailedReport) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.Unit,
                columnHeader: this.appTextService.get('unit'),
            });
        }
        if (
            isUserDetailedReport ||
            this.statistic2HelperService.isUserLoginsPerUser(currentReportParameter) ||
            this.statistic2HelperService.isReadRatingPerUser(currentReportParameter)
        ) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.Roles,
                columnHeader: this.appTextService.get('common.roles'),
            });
        }
        viewParams.showPercentageValueColumn =
            this.statistic2HelperService.isShowDataPresentationType(currentReportParameter);
        if (viewParams.showPercentageValueColumn) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.PercentageValue,
                columnHeader: this.appTextService.get('statistic.user_logins_percentage'),
                isPercentage: true,
            });
        }

        if (this.statistic2HelperService.isFileViews(currentReportParameter)) {
            if (this.statistic2HelperService.isFileViewsPerUser(currentReportParameter)) {
                viewParams.schema.push({
                    columnType: Statistic2ReportColumn.Roles,
                    columnHeader: this.appTextService.get('common.roles'),
                });
            }

            /*if (this.statistic2HelperService.isFileViewsPerArea(currentReportParameter)) {
                viewParams.schema.push({
                    columnType: Statistic2ReportColumn.Unit,
                    columnHeader: this.appTextService.get('common.units'),
                });
            }*/

            if (!this.statistic2HelperService.isFileViewsNoGrouping(currentReportParameter)) {
                viewParams.schema.push({
                    columnType: Statistic2ReportColumn.FileViewsCount,
                    columnHeader: this.appTextService.get('common.views'),
                });
                viewParams.schema.push({
                    columnType: Statistic2ReportColumn.FileDownloadsCount,
                    columnHeader: this.appTextService.get('common.downloads'),
                });
            }
        }

        if (!isUserNoGropingReport && this.statistic2HelperService.isUserLogins(currentReportParameter)) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.WebLoginsCount,
                columnHeader: this.appTextService.get('statistic.user_web_logins_count'),
                columnTooltip: this.appTextService.get('statistic.user_web_logins_count_tooltip'),
            });
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.MobileAppLoginsCount,
                columnHeader: this.appTextService.get('statistic.user_mobile_app_logins_count'),
                columnTooltip: this.appTextService.get('statistic.user_mobile_app_logins_count_tooltip'),
            });
        }

        if (!isUserNoGropingReport && !this.statistic2HelperService.isFileViews(currentReportParameter)) {
            viewParams.schema.push({ columnType: Statistic2ReportColumn.Value, columnHeader: '' });

            if (
                this.statistic2HelperService.isUserLoginsPerUser(currentReportParameter) &&
                currentReportParameter.BusinessUnitIds?.length === 1
            ) {
                viewParams.schema.push({
                    columnType: Statistic2ReportColumn.CurrentUnitVisitsColumn,
                    columnHeader: this.appTextService.get('statistic.this_unit'),
                });
            }
        }
        if (
            !isUserNoGropingReport &&
            this.statistic2HelperService.isReadRating(currentReportParameter) &&
            !this.statistic2HelperService.isReadRatingPerDocument(currentReportParameter)
        ) {
            viewParams.schema.push({ columnType: Statistic2ReportColumn.NewItemsValue, columnHeader: '' }); //todo
        }
        if (isUserNoGropingReport) {
            viewParams.schema.push({ columnType: Statistic2ReportColumn.StartDate, columnHeader: '' });
            if (this.statistic2HelperService.isFileViewsNoGrouping(currentReportParameter)) {
                viewParams.schema.push({
                    columnType: Statistic2ReportColumn.FileViewsActivity,
                    columnHeader: this.appTextService.get('common.activity'),
                });
            } else {
                viewParams.schema.push({ columnType: Statistic2ReportColumn.EndDate, columnHeader: '' });
            }
        }
        if (this.statistic2HelperService.isReadRatingNoGrouping(currentReportParameter)) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.TimeOnPage,
                columnHeader: this.appTextService.get('statistic.time_on_page'),
            });
        }
        viewParams.showLatestActivityColumn = isUserDetailedReport;
        if (viewParams.showLatestActivityColumn) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.LatestActivity,
                columnHeader: this.appTextService.get('latest_activity'),
            });
        }
        if (this.statistic2HelperService.isReadRatingPerDocument(currentReportParameter)) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.VisitsColumn,
                columnHeader: this.appTextService.get('statistic.visits_to_page'),
            });
        }
        if (this.statistic2HelperService.isUserLoginsNoGrouping(currentReportParameter)) {
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.DeviceType,
                columnHeader: this.appTextService.get('statistic.platform'),
            });
            viewParams.schema.push({
                columnType: Statistic2ReportColumn.Unit,
                columnHeader: this.appTextService.get('common.unit'),
            });
        }
        const previousReportViewParams = this.getPreviousReportViewParams();
        viewParams.objectDetails = previousReportViewParams ? previousReportViewParams.objectDetails.slice(0) : [];
        if (viewParams.selectedObjectName) {
            viewParams.objectDetails.push({
                header: viewParams.selectedObjectType,
                value: viewParams.selectedObjectName,
                type: Statistic2ReportDetailsType.ReportObject,
            });
        }

        //viewParams.showAllItemsValueColumn = !isUserNoGropingReport && this.statistic2HelperService.isReadRating(currentReportParameter) && !this.statistic2HelperService.isReadRatingPerDocument(currentReportParameter);

        this.setReportInfoText(currentReportParameter, viewParams);
        this.setHeaderTexts(currentReportParameter, viewParams);

        // details
        viewParams.details = [];
        viewParams.details.push({
            header: this.appTextService.get('statistic.report_name'),
            value: this.initialReportParameter.Name,
            type: Statistic2ReportDetailsType.ReportName,
        });
        viewParams.details.push({
            header: this.appTextService.get('statistic.report_type'),
            value: this.statistic2HelperService.getReportTypeText(viewParams.reportParameter.ReportType),
            type: Statistic2ReportDetailsType.ReportType,
        });
        if (this.isReportParameterUserLogins()) {
            viewParams.details.push({
                header: this.appTextService.get('statistic.from_date'),
                value: '',
                type: Statistic2ReportDetailsType.ReportFromDate,
            });
            viewParams.details.push({
                header: this.appTextService.get('statistic.to_date'),
                value: '',
                type: Statistic2ReportDetailsType.ReportToDate,
            });
        }
        viewParams.details.push({
            header: this.appTextService.get('statistic.user_selection'),
            value: this.statistic2HelperService.getUserSelectionText(viewParams.reportParameter.UserSelectionType),
            type: Statistic2ReportDetailsType.ReportUserSelection,
        });

        this.showReportViewList$.next(false);
        setTimeout(() => {
            this.showReportViewList$.next(true);
        });
    }

    private getResultPerType(
        viewParams: IStatistic2ReportViewParams,
        reportParameter: IStatistic2ReportParameter,
    ): Statistic2ResultsPerType {
        if (this.statistic2HelperService.isFileViewsPerMediaFile(reportParameter)) {
            return Statistic2ResultsPerType.File;
        }
        if (
            viewParams.reportParameter.ShowResultsPerType !== Statistic2ResultsPerType.NoGroping &&
            viewParams.reportParameter.ShowResultsPerType !== Statistic2ResultsPerType.Detailed
        ) {
            return viewParams.reportParameter.ShowResultsPerType;
        }

        return Statistic2ResultsPerType.User;
    }

    private isStatisticsReportPerUser(reportParameter: IStatistic2ReportParameter) {
        return (
            this.statistic2HelperService.isUserLoginsPerUser(reportParameter) ||
            this.statistic2HelperService.isReadRatingPerUser(reportParameter) ||
            this.statistic2HelperService.isFileViewsPerUser(reportParameter)
        );
    }

    private setReportInfoText(reportParameter: IStatistic2ReportParameter, viewParams: IStatistic2ReportViewParams) {
        if (reportParameter.ReportType === Statistic2ReportType.UserLogins) {
            this.timePeriod = this.statistic2HelperService.getTimePeriodText(reportParameter.TimePeriod);
        }
    }

    private setHeaderTexts(reportParameter: IStatistic2ReportParameter, viewParams: IStatistic2ReportViewParams) {
        const valueColumn: IStatistic2ReportSchemaItem =
            viewParams.schema.find((item) => item.columnType === Statistic2ReportColumn.Value) || <any>{};
        const startDateColumn: IStatistic2ReportSchemaItem =
            viewParams.schema.find((item) => item.columnType === Statistic2ReportColumn.StartDate) || <any>{};
        const endDateColumn: IStatistic2ReportSchemaItem =
            viewParams.schema.find((item) => item.columnType === Statistic2ReportColumn.EndDate) || <any>{};
        const newItemsValueColumn: IStatistic2ReportSchemaItem =
            viewParams.schema.find((item) => item.columnType === Statistic2ReportColumn.NewItemsValue) || <any>{};
        const percentageValueColumn: IStatistic2ReportSchemaItem =
            viewParams.schema.find((item) => item.columnType === Statistic2ReportColumn.PercentageValue) || <any>{};

        if (this.statistic2HelperService.isUserLogins(reportParameter)) {
            valueColumn.columnHeader = this.appTextService.get('statistic.total_logins');

            if (viewParams.showPercentageValueColumn && viewParams.showAreaColumn) {
                percentageValueColumn.columnTooltip = this.appTextService.get(
                    'statistic.user_logins_percentage_unit_tooltip',
                );
            } else {
                percentageValueColumn.columnTooltip = this.appTextService.get(
                    'statistic.user_logins_percentage_area_tooltip',
                );
            }

            valueColumn.columnTooltip = this.appTextService.get('statistic.percentage_tooltip_header2');

            startDateColumn.columnHeader = this.appTextService.get('common.log_in');
            endDateColumn.columnHeader = this.appTextService.get('statistic.logout');
        } else if (this.statistic2HelperService.isReadRating(reportParameter)) {
            /*if (this.statistic2HelperService.isReadRatingDocumentVisits(reportParameter)){
                viewParams.valueHeaderText = this.appTextService.get('statistic.visits_to_page');
                viewParams.valueHeaderTooltipText = this.appTextService.get('statistic.visits_to_page_header_tooltip');

                viewParams.newItemsValueHeaderText = this.appTextService.get('statistic.read_rating_new_pages') + ' %';
                viewParams.newItemsValueHeaderTooltipText = this.appTextService.get('statistic.read_rating_new_pages_header_tooltip');

                //viewParams.allItemsValueHeaderText = this.appTextService.get('statistic.read_rating_all_pages') + ' %';
                //viewParams.allItemsValueHeaderTooltipText = this.appTextService.get('statistic.read_rating_all_pages_header_tooltip');

            } else */
            if (
                this.statistic2HelperService.isReadRatingPerArea(reportParameter) ||
                this.statistic2HelperService.isReadRatingPerUnit(reportParameter) ||
                this.statistic2HelperService.isReadRatingPerUser(reportParameter)
            ) {
                valueColumn.columnHeader = this.appTextService.get('statistic.selected_pages');
                valueColumn.isPercentage = true;

                this.statistic2HelperService.isReadRatingPerUser(reportParameter)
                    ? (valueColumn.columnTooltip = this.appTextService.get(
                          'statistic.read_rating_selected_pages_users_header_tooltip',
                      ))
                    : (valueColumn.columnTooltip = this.appTextService.get('statistic.selected_pages_header_tooltip'));

                newItemsValueColumn.columnHeader = this.appTextService.get('statistic.read_rating_new_pages');
                newItemsValueColumn.columnTooltip = this.appTextService.get(
                    'statistic.read_rating_new_pages_header_tooltip',
                );
                newItemsValueColumn.isPercentage = true;

                //viewParams.allItemsValueHeaderText = this.appTextService.get('statistic.read_rating_all_pages') + ' %';
                //viewParams.allItemsValueHeaderTooltipText = this.appTextService.get('statistic.read_rating_all_pages_header_tooltip');
            } else if (this.statistic2HelperService.isReadRatingPerDocument(reportParameter)) {
                valueColumn.columnHeader = this.appTextService.get('statistic.read_rating');
                valueColumn.columnTooltip = this.appTextService.get('statistic.selected_pages_header_tooltip');
                valueColumn.isPercentage = true;
            } else {
                console.log(viewParams);
            }

            startDateColumn.columnHeader = this.appTextService.get('statistic.entered_page');
            endDateColumn.columnHeader = this.appTextService.get('statistic.left_page');
        } else {
            startDateColumn.columnHeader = this.appTextService.get('statistic.time');
        }
    }

    onReportItemClick(item: IStatistic2ReportItem) {
        this.filterText = '';
        this.filterText$.next(this.filterText);
        const currentReportParameter = this.getCurrentReportViewParams().reportParameter;

        const viewParams: IStatistic2ReportViewParams = {
            reportParameter: cloneDeep(currentReportParameter),
        };

        if (this.statistic2HelperService.isUserLogins(currentReportParameter)) {
            viewParams.selectedObjectName = item.ObjectName;
            viewParams.selectedObjectType = this.objectTypesService.GetNameByObjectType(item.ObjectType);

            if (this.statistic2HelperService.isUserLoginsPerArea(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.Unit;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.Areas;
                viewParams.reportParameter.AreaIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isUserLoginsPerUnit(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.User;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.BusinessUnits;
                viewParams.reportParameter.BusinessUnitIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isUserLoginsPerUser(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.NoGroping;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.Users;
                viewParams.reportParameter.UserIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isUserLoginsNoGrouping(currentReportParameter)) {
                this.userService.openUserViewDialog(item.ObjectId);
                return;
            } else {
                return;
            }
        } else if (this.statistic2HelperService.isReadRating(currentReportParameter)) {
            viewParams.selectedObjectName = item.ObjectName;
            viewParams.selectedObjectType = this.objectTypesService.GetNameByObjectType(item.ObjectType);

            if (this.statistic2HelperService.isReadRatingPerArea(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.Unit;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.Areas;
                viewParams.reportParameter.AreaIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isReadRatingPerUnit(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.User;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.BusinessUnits;
                viewParams.reportParameter.BusinessUnitIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isReadRatingPerUser(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.Object;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.Users;
                viewParams.reportParameter.UserIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isReadRatingPerDocument(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.NoGroping;
                viewParams.reportParameter.ObjectIds = [item.ObjectId];
            } else {
                return;
            }
        } else if (this.statistic2HelperService.isFileViews(currentReportParameter)) {
            viewParams.selectedObjectName = item.ObjectName;
            viewParams.selectedObjectType = this.objectTypesService.GetNameByObjectType(item.ObjectType);

            if (this.statistic2HelperService.isFileViewsPerArea(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.Unit;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.Areas;
                viewParams.reportParameter.AreaIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isFileViewsPerUnit(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.User;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.BusinessUnits;
                viewParams.reportParameter.BusinessUnitIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isFileViewsPerUser(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.Object;
                viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.Users;
                viewParams.reportParameter.UserIds = [item.ObjectId];
            } else if (this.statistic2HelperService.isFileViewsPerMediaFile(currentReportParameter)) {
                viewParams.reportParameter.ShowResultsPerType = Statistic2ResultsPerType.NoGroping;
                viewParams.reportParameter.ObjectIds = [item.ObjectId];
            } else {
                return;
            }
        } else {
            throw new Error('wrong currentReportParameter in onReportItemClick');
            return; // implement further reports switch logic here
        }

        this.reportViewParamsStack.push(viewParams);
        this.onReportParameterChanged();
    }

    filterText: string;

    onFilterTextChange($event) {
        this.filterText$.next(this.filterText);
    }

    showResultsPerList = [Statistic2ResultsPerType.Area, Statistic2ResultsPerType.Unit, Statistic2ResultsPerType.User];
    showResultsPer: Statistic2ResultsPerType;

    onResultsPerChange(resultsPerType: Statistic2ResultsPerType) {
        this.showResultsPer = resultsPerType;
        const currentReportParameter = this.getCurrentReportViewParams().reportParameter;
        const viewParams: IStatistic2ReportViewParams = {
            reportParameter: cloneDeep(currentReportParameter),
        };
        viewParams.reportParameter.ShowResultsPerType = resultsPerType;
        viewParams.reportParameter.UserSelectionType = Statistic2UserSelectionType.EveryOne;
        this.reportViewParamsStack.length = 0;
        this.reportViewParamsStack.push(viewParams);
        this.onReportParameterChanged();
    }

    getShowResultsPerText(showResultsPer: Statistic2ResultsPerType) {
        return this.statistic2HelperService
            .getShowResultsPerText(
                showResultsPer !== Statistic2ResultsPerType.NoGroping ? showResultsPer : Statistic2ResultsPerType.User,
            )
            .toLowerCase();
    }

    showReportBack() {
        return this.reportViewParamsStack.length > 1;
    }

    onReportBackClick() {
        this.filterText = '';
        this.filterText$.next(this.filterText);
        if (this.reportViewParamsStack.length === 1) return;
        this.reportViewParamsStack.pop();
        this.onReportParameterChanged();
    }

    ngOnDestroy$ = new Subject<void>();

    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    getReportTypeText() {
        const reportViewParams = this.getCurrentReportViewParams();
        if (!reportViewParams.selectedObjectName) {
            let reportTypeText = this.statistic2HelperService.getReportTypeText(
                reportViewParams.reportParameter.ReportType,
            );
            if (this.statistic2HelperService.isReadRating(reportViewParams.reportParameter)) {
                reportTypeText +=
                    ' ' +
                    this.appTextService.get('statistic.for').toLowerCase() +
                    ': ' +
                    this.statistic2HelperService.getReportForText(reportViewParams.reportParameter);
            }
            return reportTypeText;
        }
    }

    getUserSelectionText() {
        const reportViewParams = this.getCurrentReportViewParams();
        if (!reportViewParams.selectedObjectName) {
            return this.statistic2HelperService.getUserSelectionText(
                reportViewParams.reportParameter.UserSelectionType,
            );
        }
    }

    showExploreReportText() {
        const reportViewParams = this.getCurrentReportViewParams();
        return !reportViewParams.selectedObjectName;
    }

    getSelectedObjectName() {
        const reportViewParams = this.getCurrentReportViewParams();
        return reportViewParams.selectedObjectName;
    }

    getSelectedObjectType() {
        const reportViewParams = this.getCurrentReportViewParams();
        return reportViewParams.selectedObjectType;
    }

    isReportParameterUserLogins() {
        return this.getCurrentReportViewParams().reportParameter.ReportType === Statistic2ReportType.UserLogins;
    }

    isReportParameterFileView(): boolean {
        return this.getCurrentReportViewParams().reportParameter.ReportType === Statistic2ReportType.FileViews;
    }

    redirectToSpectificItem(index: number) {
        this.getCurrentReportViewParams().loadItemsPromise.then((items) => {
            this.onReportItemClick(items.Data[index]);
            this.statistic2HelperService.setSelectedReportItemIndex(undefined);
        });
    }

    onDownloadClick() {
        let currentReportViewParams = this.getCurrentReportViewParams();
        if (this.reportViewListComponent && this.reportViewListComponent.selectedSort) {
            currentReportViewParams = Object.assign(currentReportViewParams, {
                selectedSort: {
                    ColumnName: this.reportViewListComponent.selectedSort.active,
                    SortDirection: this.getSortDirection(),
                },
            });
        }
        this.statistic2ReportService.downloadExtendedReport(currentReportViewParams);
    }

    private getSortDirection(): SortDirection {
        return this.reportViewListComponent.selectedSort.direction === MatSortDirection.Ascending
            ? SortDirection.Ascending
            : SortDirection.Descending;
    }
}
